/**
 * 결제·환불 도메인 타입 (aivatar #488 · aipark-front #266)
 *
 * 백엔드 enum 을 그대로 옮긴다. 라벨은 화면에서만 붙이고, 판정·분기는 항상 원본 enum 값으로
 * 한다 — 라벨 문자열로 분기하면 문구가 바뀔 때 로직이 조용히 틀린다.
 *
 * BE 기준: `feature/payment-phase1` (5f7a0b0c)
 */

/* ────────────────────────────── 결제 건 ────────────────────────────── */

export type InvoiceStatus = 'PAID' | 'FAILED'

/** `GET /v1/workspaces/{id}/invoices` 행 (`InvoiceDto`) */
export type Invoice = {
  id: number
  status: InvoiceStatus | string
  price: number
  slipUrl: string | null
  paymentNumber: string | null
  currencyCode: string | null
  paidAt: string | null
  /**
   * 이 결제가 붙은 구독. 산정 API 가 워크스페이스가 아니라 구독 id 로 대상을 지목하므로
   * (한 워크스페이스에 구독 행이 여럿이다) 여기서 `subscriptionId` 를 꺼내 쓴다.
   */
  subscription: InvoiceSubscription | null
  createdAt: string
  updatedAt: string
  /**
   * 주문번호. 결제대행사 콘솔에서 이 건을 검색하는 값이다(엔티티 `order_num`).
   *
   * 값이 없는 결제 건도 있어 null 을 허용한다. optional 인 것은 `InvoiceDto` 에 이 필드가 없던
   * 시절(aivatar #488 ①)의 잔재이기도 하지만, 지금은 nullable 계약 그대로다.
   */
  orderNumber?: string | null
}

/** `WorkspaceSubscriptionDto` 중 이 화면이 쓰는 것만 */
export type InvoiceSubscription = {
  subscriptionId: number | null
  plan: { id: number; name: string } | null
  /** 1 = 월, 12 = 연간. 결제 건 목록의 "구분" 이 이 값이다 */
  intervalMonth: number | null
  startedAt: string | null
  endAt: string | null
  subscribedAt: string | null
}

/** 결제 건의 구분. 전환 여부는 프론트가 판정하지 않는다(아래 주석) */
export type BillingInterval = 'MONTHLY' | 'ANNUAL' | 'UNKNOWN'

/**
 * 월/연 구분.
 *
 * 와이어프레임의 "구분" 은 월 구독·연간·**전환 결제** 3값이지만, 전환 판정은 서버 규칙이
 * 6조건이라(`RollbackSettlementService.gather`) 프론트가 복제하지 않기로 했다(aivatar #488 ②).
 * 그 결정은 지금도 유효하다 — 다만 되감기 **버튼**의 노출은 이제 서버가 내려주는 불리언
 * (`InvoiceRefundView.rollbackAvailable`)을 읽어 정한다(aipark-front #269). 복제 금지의 취지를
 * 지키면서 명세 7-3 을 만족하는 쪽이다. 이 구분 라벨은 여전히 판정하지 않는다.
 */
export const resolveBillingInterval = (intervalMonth?: number | null): BillingInterval => {
  if (intervalMonth === 1) return 'MONTHLY'
  if (intervalMonth != null && intervalMonth >= 6) return 'ANNUAL'
  return 'UNKNOWN'
}

export const BILLING_INTERVAL_LABEL: Record<BillingInterval, string> = {
  MONTHLY: '월 구독',
  ANNUAL: '연간',
  UNKNOWN: '-',
}

/* ────────────────────────────── 청구 내역 (구독 + 크레딧 팩) ────────────────────────────── */

/** 청구 내역 행 종류. 구독 결제(청구서)와 크레딧 팩 구매(단건 상점 결제 건)다 */
export type BillingHistoryType = 'SUBSCRIPTION' | 'CREDIT_PACK'

/**
 * `GET /v1/workspaces/{id}/billing-history` 행 (`BillingHistoryItem`, aivatar #533 · D-125)
 *
 * 🔴 **id 가 두 칸이다.** 구독 행은 `invoiceId`, 크레딧 팩 행은 `paymentOrderId` 만 채워진다.
 * 청구서 23 과 결제 건 23 이 함께 있을 수 있어 한 칸으로 합치면, 결제 건 id 로 청구서 환불 API 를
 * 불러 **남의 청구서에 환불이 붙는다.** 환불 API 를 고를 때는 `refundTargetOf` 를 거친다.
 */
export type BillingHistoryItem = {
  type: BillingHistoryType | string
  invoiceId: number | null
  paymentOrderId: number | null
  /** 구독은 플랜 표시명(월/연 표기는 `subscription` 으로 붙인다), 크레딧 팩은 팩 이름 */
  itemName: string | null
  /** 구독은 `PAID`·`FAILED`, 크레딧 팩은 결제 완료만 온다 */
  status: InvoiceStatus | string | null
  price: number
  currencyCode: string | null
  /** 결제 시각. 결제 시각이 없는 실패 청구서는 **생성 시각**이 온다 */
  paidAt: string | null
  /** 결제대행사 결제 고유번호(imp_uid) */
  paymentNumber: string | null
  orderNumber: string | null
  /** 영수증. 크레딧 팩은 결제대행사(단건 상점) 영수증이며 아직 못 받은 건은 비어 있다(aivatar #538) */
  slipUrl: string | null
  /** 구독 행만. 종전 `/invoices` 행과 같은 모양이다 */
  subscription: InvoiceSubscription | null
  /** 크레딧 팩 행만. 지급 크레딧 */
  grantedCredit: number | null
  /** 크레딧 팩 행만. 소멸일(당일까지 유효). 환불·소멸로 닫힌 건도 원래 소멸일이다 */
  expiresOn: string | null
  /**
   * 크레딧 팩 행만. 그 결제 건의 남은 크레딧(명세 7-2 역산식, 고객 화면 구매 건별 잔액과 같은 계산 · aivatar #554).
   * 소멸 · 환불 완료 · 다 쓴 건은 0. 잔여를 믿을 수 없는 워크스페이스는 null — 빈칸으로 둔다.
   * optional 인 것은 배포 순서 때문이다(BE 전에는 응답에 없다).
   */
  remainingCredit?: number | null
}

/**
 * 환불이 붙는 대상 (D-121). **두 종류를 한 값으로 섞지 않는다** — 조회·접수 API 가 종류마다 다르고
 * id 가 서로 겹친다. 이 값 하나로 분기하면 "결제 건 id 로 청구서 API 를 부르는" 경로가 타입에서 막힌다.
 */
export type RefundTarget = { kind: 'INVOICE'; invoiceId: number } | { kind: 'PAYMENT_ORDER'; paymentOrderId: number }

/** 행 종류에 맞는 칸만 읽는다. 종류와 id 가 어긋난 행(계약 위반)은 null — 어느 API 도 부르지 않는다 */
export const refundTargetOf = (item: BillingHistoryItem): RefundTarget | null => {
  if (item.type === 'SUBSCRIPTION' && item.invoiceId != null) return { kind: 'INVOICE', invoiceId: item.invoiceId }
  if (item.type === 'CREDIT_PACK' && item.paymentOrderId != null)
    return { kind: 'PAYMENT_ORDER', paymentOrderId: item.paymentOrderId }
  return null
}

/** 대장·환불 행이 가리키는 대상. 서버는 `invoiceId`·`paymentOrderId` 중 하나만 채운다 */
export const refundTargetOfRow = (row: {
  invoiceId: number | null
  paymentOrderId?: number | null
}): RefundTarget | null => {
  if (row.paymentOrderId != null) return { kind: 'PAYMENT_ORDER', paymentOrderId: row.paymentOrderId }
  if (row.invoiceId != null) return { kind: 'INVOICE', invoiceId: row.invoiceId }
  return null
}

/** 화면 표기. 청구서와 결제 건은 번호 체계가 달라 `#23` 만 적으면 어느 쪽인지 모른다 */
export const refundTargetLabel = (target: RefundTarget | null): string => {
  if (!target) return '-'
  return target.kind === 'INVOICE' ? `청구서 #${target.invoiceId}` : `크레딧 팩 결제 건 #${target.paymentOrderId}`
}

/** 행 key·맵 key. 종류를 앞에 붙여 청구서 23 과 결제 건 23 이 겹치지 않게 한다 */
export const refundTargetKey = (target: RefundTarget): string =>
  target.kind === 'INVOICE' ? `INVOICE-${target.invoiceId}` : `PAYMENT_ORDER-${target.paymentOrderId}`

/**
 * 구독 행을 종전 `Invoice` 모양으로. 산정·되감기·수동 기록 모달이 청구서 행을 받으므로 그 경로는
 * 그대로 두고 입구에서만 바꾼다. 크레딧 팩 행은 청구서가 아니라 넘기지 않는다(null).
 */
export const toInvoice = (item: BillingHistoryItem): Invoice | null => {
  if (item.type !== 'SUBSCRIPTION' || item.invoiceId == null) return null
  return {
    id: item.invoiceId,
    status: item.status ?? '',
    price: item.price,
    slipUrl: item.slipUrl,
    paymentNumber: item.paymentNumber,
    currencyCode: item.currencyCode,
    paidAt: item.paidAt,
    subscription: item.subscription,
    // 이 목록은 생성 시각을 따로 주지 않는다. 결제 시각이 없는 실패 건은 paidAt 이 곧 생성 시각이다
    createdAt: item.paidAt ?? '',
    updatedAt: item.paidAt ?? '',
    orderNumber: item.orderNumber,
  }
}

/* ────────────────────────────── 환불 기록 ────────────────────────────── */

/** 환불 행의 생애 4상태 (`InvoiceRefundStatus`) */
export type InvoiceRefundStatus = 'QUOTED' | 'COMPLETED' | 'ON_HOLD' | 'WITHDRAWN'

export const REFUND_STATUS_LABEL: Record<InvoiceRefundStatus, string> = {
  QUOTED: '산정 완료',
  COMPLETED: '처리 완료',
  ON_HOLD: '보류',
  WITHDRAWN: '철회',
}

export const REFUND_STATUS_COLOR: Record<InvoiceRefundStatus, string> = {
  QUOTED: 'gold',
  COMPLETED: 'green',
  ON_HOLD: 'orange',
  WITHDRAWN: 'default',
}

/** 대장 정렬 우선순위. 손이 필요한 것(접수·보류)이 위로 온다 — 서버 정렬과 같은 규칙이다 */
export const REFUND_STATUS_ORDER: Record<InvoiceRefundStatus, number> = {
  QUOTED: 0,
  ON_HOLD: 1,
  COMPLETED: 2,
  WITHDRAWN: 3,
}

/** 한 청구서에 얼마나 돌려줬나 (`InvoiceRefundState`). 처리 완료 행만 센다 */
export type InvoiceRefundState = 'NONE' | 'PARTIAL' | 'FULL' | 'OVER_REFUNDED'

/**
 * 환불 사유. **이 값이 후속 조치를 정한다** — 담당자가 조치를 직접 고르지 않는다.
 * 그래서 사유 선택은 확정 폼이 아니라 접수 모달에 있다(aivatar #488 (a)).
 */
export type RefundReason =
  | 'SUBSCRIPTION_CANCEL'
  | 'COOLING_OFF'
  | 'CONVERSION_COOLING_OFF'
  | 'CONVERSION_ROLLBACK'
  | 'CREDIT_PACK'
  | 'TERMINATION_OPTION'
  | 'POST_EXPIRY_DISCRETION'
  | 'COMPANY_FAULT_TERMINATION'
  | 'OTHER'

export const REFUND_REASON_LABEL: Record<RefundReason, string> = {
  SUBSCRIPTION_CANCEL: '구독 취소',
  COOLING_OFF: '청약철회',
  CONVERSION_COOLING_OFF: '전환 청약철회(잔류)',
  CONVERSION_ROLLBACK: '전환 되감기(이탈)',
  CREDIT_PACK: '크레딧 팩 환불',
  TERMINATION_OPTION: '종료 선택권',
  POST_EXPIRY_DISCRETION: '소멸 후 재량 처리',
  COMPANY_FAULT_TERMINATION: '회사 귀책',
  OTHER: '기타',
}

/** 실제로 돈이 나간 경로 (`RefundChannel`) */
export type RefundChannel = 'PORTONE' | 'BANK_TRANSFER' | 'POST_EXPIRY_DISCRETION' | 'OTHER'

export const REFUND_CHANNEL_LABEL: Record<RefundChannel, string> = {
  PORTONE: '원 결제수단 취소',
  BANK_TRANSFER: '계좌 환급',
  POST_EXPIRY_DISCRETION: '소멸 후 재량 처리',
  OTHER: '기타',
}

/** 위약금 구간. 같은 0% 라도 근거 조항이 갈린다 */
export type PenaltyBracket =
  | 'WITHIN_WITHDRAWAL'
  | 'SUBSCRIPTION_CANCEL_FEE'
  | 'TERMINATION_OPTION'
  | 'COMPANY_FAULT'
  | 'CREDIT_PACK_USAGE'
  | 'CREDIT_PACK_LOW_USAGE'
  | 'CREDIT_PACK_HIGH_USAGE'
  /** 소멸 후 재량 처리(D-60). 산정 구간이 아니라 기록 추가에서 서버가 사유로 채운다(aivatar #556 · v4 P5) */
  | 'POST_EXPIRY_DISCRETION'

export const PENALTY_BRACKET_LABEL: Record<PenaltyBracket, string> = {
  WITHIN_WITHDRAWAL: '청약철회 기간 이내 (위약금 없음)',
  SUBSCRIPTION_CANCEL_FEE: '구독취소 수수료',
  TERMINATION_OPTION: '종료 선택권 (위약금 없음)',
  COMPANY_FAULT: '회사 귀책 (위약금 없음)',
  CREDIT_PACK_USAGE: '단건 크레딧 사용분 (구 구간)',
  CREDIT_PACK_LOW_USAGE: '단건 3-1 · 사용률 10% 이하',
  CREDIT_PACK_HIGH_USAGE: '단건 3-2 · 사용률 10% 초과',
  POST_EXPIRY_DISCRETION: '소멸 후 재량',
}

/**
 * 담당자가 고를 수 있는 구간. `CREDIT_PACK_USAGE` 는 BE 가 deprecated 로 남긴 값이라 빠진다 —
 * 명세 7-2 가 단건 구간을 3-1·3-2 로 나눠 세라고 한다(D-60). 이미 저장된 행을 읽는 라벨은 위에 남긴다.
 *
 * `POST_EXPIRY_DISCRETION` 은 고를 수 있다 — 비워 두면 서버가 사유로 채우지만, 기록 추가 폼에서 근거 조항을
 * 직접 남기려는 담당자에게 목록에 없으면 다른 구간을 고르게 된다(QA 회차 뒤 표시 건 · 42). 서버는 요청 값을 우선한다
 */
export const SELECTABLE_PENALTY_BRACKETS: PenaltyBracket[] = (Object.keys(PENALTY_BRACKET_LABEL) as PenaltyBracket[]).filter(
  (bracket) => bracket !== 'CREDIT_PACK_USAGE',
)

/* ────────────────────────────── 후속 조치 ────────────────────────────── */

export type FollowUpStatus = 'NOT_APPLICABLE' | 'PENDING_GROUP' | 'DONE' | 'FAILED'

export const FOLLOW_UP_STATUS_LABEL: Record<FollowUpStatus, string> = {
  NOT_APPLICABLE: '실행 안 함',
  PENDING_GROUP: '짝 대기',
  DONE: '완료',
  FAILED: '실패',
}

/**
 * 후속 조치 상태 라벨. **크레딧 팩 결제 건 행의 `FAILED` 는 "미실행 · 재시도"** 다(환불대장 v4 P4 · P5, PK-44).
 *
 * 크레딧 팩 결제 건을 기록 추가로 바로 처리 완료하면 남은 크레딧이 있을 때 서버가 후속 조치를 실행하지 않고
 * `FAILED` 로 남긴다 — 재시도가 남은 크레딧을 없앤다. 실패가 아니라 "아직 안 돌렸다" 이므로 v4 가 정한 이름으로
 * 보인다. 청구서 행은 종전대로 "실패"(S5).
 */
export const followUpStatusLabel = (status: FollowUpStatus, paymentOrderId?: number | null): string =>
  status === 'FAILED' && paymentOrderId != null ? '미실행 · 재시도' : (FOLLOW_UP_STATUS_LABEL[status] ?? status)

export const FOLLOW_UP_STATUS_COLOR: Record<FollowUpStatus, string> = {
  NOT_APPLICABLE: 'default',
  PENDING_GROUP: 'gold',
  DONE: 'green',
  FAILED: 'red',
}

export type RefundFollowUpAction =
  | 'NONE'
  | 'REVOKE_CREDIT_PACK'
  | 'RESTORE_PRIOR_AGREEMENT'
  | 'DEACTIVATE_SUBSCRIPTION'
  | 'RESTORE_AND_DEACTIVATE'
  | 'PENDING_GROUP'

/**
 * 조치 라벨. 서버 `RefundFollowUpService.actionOf` 의 매핑을 사람 말로 옮긴 것이다.
 *
 * `PENDING_GROUP` 은 조치가 아니라 **아직 실행하지 않았다는 사정**이다 — 이탈형 되감기처럼
 * 사건이 2행이면 둘 다 확정돼야 발화한다. 한 건만 끝난 채 구독을 끊으면 돈은 절반만 돌아가고
 * 서비스는 멈춘다.
 */
export const REFUND_FOLLOW_UP_ACTION_LABEL: Record<RefundFollowUpAction, string> = {
  NONE: '후속 조치 없음',
  REVOKE_CREDIT_PACK: '이 결제 건의 남은 크레딧 소멸',
  RESTORE_PRIOR_AGREEMENT: '직전 약정 복원 (구독 유지)',
  DEACTIVATE_SUBSCRIPTION: '구독 즉시 비활성 전환',
  RESTORE_AND_DEACTIVATE: '직전 약정 복원 후 구독 종료',
  PENDING_GROUP: '짝 대기 — 사건의 다른 행이 확정돼야 실행',
}

/**
 * 사유 → 후속 조치. **표시 전용이다.** 실제 실행은 서버가 정하며
 * (`RefundFollowUpService.actionOf`), 이 표는 담당자에게 "이 사유를 고르면 무엇이 일어나는가"
 * 를 미리 보여주기 위한 것이다. 서버 매핑이 바뀌면 여기도 같이 고쳐야 한다.
 *
 * 문자열이 아니라 조치 값으로 둔다 — 확정 폼의 문구가 이 값으로 갈리기 때문이다(v3 S8).
 * 라벨로 분기하면 문구를 다듬는 순간 분기가 조용히 틀린다.
 */
export const REFUND_REASON_ACTION: Record<RefundReason, RefundFollowUpAction> = {
  SUBSCRIPTION_CANCEL: 'DEACTIVATE_SUBSCRIPTION',
  COOLING_OFF: 'DEACTIVATE_SUBSCRIPTION',
  CONVERSION_COOLING_OFF: 'RESTORE_PRIOR_AGREEMENT',
  CONVERSION_ROLLBACK: 'RESTORE_AND_DEACTIVATE',
  CREDIT_PACK: 'NONE',
  TERMINATION_OPTION: 'NONE',
  POST_EXPIRY_DISCRETION: 'NONE',
  COMPANY_FAULT_TERMINATION: 'NONE',
  OTHER: 'NONE',
}

/** 위 표의 라벨. 조치 라벨을 그대로 쓴다 — 같은 것을 두 문장으로 적으면 둘이 갈린다 */
export const REFUND_REASON_FOLLOW_UP: Record<RefundReason, string> = Object.fromEntries(
  (Object.keys(REFUND_REASON_ACTION) as RefundReason[]).map((reason) => [
    reason,
    REFUND_FOLLOW_UP_ACTION_LABEL[REFUND_REASON_ACTION[reason]],
  ]),
) as Record<RefundReason, string>

/**
 * 이 사유의 확정이 **구독을 끄는가**. 확정 폼의 처리일 라벨·경고 문구가 여기서 갈린다.
 *
 * 🔴 잔류형(`CONVERSION_COOLING_OFF`)은 직전 약정을 복원만 하고 구독은 계속된다. 그런데 화면이
 * 유형과 무관하게 "구독 비활성 진입일이 됩니다" 를 띄우고 있어, 담당자가 구독이 끊길까 봐 확정을
 * 망설였다(환불 QA 회차 2026-09-21~22 · v3 S8). 처리일이 비활성 진입일이 되는 것은 구독이
 * 꺼지는 유형뿐이다.
 */
export const deactivatesSubscription = (reason: RefundReason): boolean => {
  const action = REFUND_REASON_ACTION[reason]
  return action === 'DEACTIVATE_SUBSCRIPTION' || action === 'RESTORE_AND_DEACTIVATE'
}

/**
 * 대상까지 본 후속 조치. **크레딧 팩 결제 건이면 사유와 무관하게 남은 크레딧 소멸이다**
 * (D-124 · 서버 `RefundFollowUpService.judgeAndRun`). 사유만 보는 `REFUND_REASON_ACTION` 은
 * 청구서 환불의 표다 — 결제 건 행에 그 표를 쓰면 "후속 조치 없음" 이라 적고 크레딧을 없앤다.
 */
export const followUpActionOf = (reason: RefundReason, target: RefundTarget | null): RefundFollowUpAction =>
  target?.kind === 'PAYMENT_ORDER' ? 'REVOKE_CREDIT_PACK' : REFUND_REASON_ACTION[reason]

/** 크레딧 팩 결제 건에 쓸 수 있는 사유. 구독 사유는 구독을 끄거나 복원하므로 서버가 400 으로 막는다 */
export const PAYMENT_ORDER_REASONS: RefundReason[] = [
  'CREDIT_PACK',
  'TERMINATION_OPTION',
  'POST_EXPIRY_DISCRETION',
  'COMPANY_FAULT_TERMINATION',
  'OTHER',
]

/** `complete` 응답의 후속 조치 결과. `dryRun` 이면 `executed: false` + 미리보기 문구다 */
export type RefundFollowUp = {
  action: RefundFollowUpAction
  executed: boolean
  description: string
  details: string[]
  restoredSubscriptionId: number | null
  /**
   * 크레딧 팩 소멸(D-124)에서 없앨(미리보기)·없앤 크레딧 = **확정 시점**의 그 결제 건 미사용분.
   * 소멸이 아니면 null. 이미 닫힌 건은 0 이고, 그때 후속 조치는 "실행 안 함" 으로 남는다(aivatar #540).
   */
  revokedCredit?: number | null
  /** 접수 때 서버가 계산한 미사용분. 이 칸이 생기기 전에 접수된 행은 사용률로 되짚은 값이다 */
  unusedAtIntake?: number | null
  /**
   * 접수 뒤 사용이 바뀌었나. 🔴 **서버 판정을 그대로 쓴다** — 접수 사용률이 반올림돼 저장돼서
   * 화면이 두 값을 직접 비교하면 같은 건을 불일치로 읽는다. 비교할 수 없으면 null.
   */
  revocationMismatch?: boolean | null
}

/* ────────────────────────────── 환불 행 ────────────────────────────── */

export type RefundHistoryAction = 'RECEIVED' | 'COMPLETED' | 'HELD' | 'WITHDRAWN' | 'FOLLOW_UP'

export const REFUND_HISTORY_ACTION_LABEL: Record<RefundHistoryAction, string> = {
  RECEIVED: '접수',
  COMPLETED: '처리 완료',
  HELD: '보류',
  WITHDRAWN: '철회',
  FOLLOW_UP: '후속 조치',
}

/**
 * 이력 문장에 박힌 처리 경로 enum 을 대장·상세와 같은 라벨로 바꾼다.
 *
 * 서버가 `"실지급 16335 · 경로 PORTONE · 처리일 …"` 처럼 **enum 을 그대로** 넣는다
 * (`InvoiceRefundService.writeHistory`). 대장 칸은 `REFUND_CHANNEL_LABEL` 로 "원 결제수단 취소"
 * 라 적으므로, 한 건이 두 이름으로 보여 사후 대조에서 다른 값처럼 읽힌다(v3 S8).
 *
 * `경로 ` 뒤에 붙은 토큰만 바꾼다. 이력 문장에는 담당자가 쓴 메모(보류·철회 사유)도 들어오는데,
 * 거기 우연히 같은 낱말이 있다고 사람이 쓴 글자를 고쳐 쓰면 안 된다. 서버 문구가 바뀌면 치환이
 * 걸리지 않고 종전처럼 enum 이 보일 뿐, 문장을 망가뜨리지는 않는다.
 */
export const humanizeRefundHistoryDetail = (detail?: string | null): string | null => {
  if (!detail) return detail ?? null
  return detail.replace(/경로\s+([A-Z_]+)/g, (matched, token: string) => {
    const label = REFUND_CHANNEL_LABEL[token as RefundChannel]
    return label ? `경로 ${label}` : matched
  })
}

export type RefundHistoryEntry = {
  action: RefundHistoryAction | string
  actorId: number | null
  actorEmail: string | null
  detail: string | null
  at: string
}

/** `InvoiceRefundResponse` — 결제 건별 환불 현황·사건 조회가 내려주는 행 */
export type InvoiceRefund = {
  id: number
  /** `invoiceId`·`paymentOrderId` 는 둘 중 하나만 채워진다 (D-121) */
  invoiceId: number | null
  paymentOrderId?: number | null
  amount: number
  penalty: number
  reason: RefundReason
  status: InvoiceRefundStatus
  channel: RefundChannel | null
  /** 수동 산정 근거. 부분 환불의 금액이 어떻게 나왔는지 유일한 설명이다 */
  calculation: string | null
  refundedAt: string | null
  issuerId: number | null
  issuerEmail: string | null
  /** 한 환불 사건이 여러 invoice 로 쪼개진 것을 묶는 번호 */
  refundGroupNo: string | null
  pgTransactionNo: string | null
  memo: string | null
  /** 담당자가 기입한 실지급액. 산정액과 갈릴 수 있다 */
  actualCancelAmount: number | null
  amountMismatch: boolean
  createdAt: string
}

/** `GET /v1/admin/invoices/{invoiceId}/refunds` (`InvoiceRefundView`) */
export type InvoiceRefundView = {
  invoiceId: number
  price: number
  /** 처리 완료 행만 센 합계 = 실제로 나간 돈 */
  refundedTotal: number
  /** 접수·보류 합계 = 아직 나가지 않았지만 이 청구서에 걸려 있는 돈 */
  unsettledTotal: number
  state: InvoiceRefundState
  /**
   * 되감기 진입 가능 여부. **이 값만 보면 된다** — 서버 6조건을 프론트가 복제하지 않는다
   * (aivatar #503 · aipark-front #269). 거짓이면 되감기 버튼을 노출하지 않는다(명세 7-3).
   *
   * 판정은 되감기 산정과 **같은 가드**(`RollbackSettlementService.availability` → `gather`)로
   * 내려오므로 "버튼은 보이는데 눌리지 않는" 상태가 생기지 않는다.
   *
   * ⚠️ optional 인 것은 배포 순서 때문이다 — BE(aivatar #516) 전에는 응답에 없다.
   * `undefined` 를 거짓으로 읽으면 배포 사이에 버튼이 사라지므로 `!== false` 로 본다.
   */
  rollbackAvailable?: boolean
  /**
   * 되감기 불가 사유. 가능하면 `null`.
   *
   * **화면에 띄우지 않는다.** 버튼 자체를 비노출로 정했으므로(비활성이 아니다) 사유를 보여줄
   * 자리가 없고, 따로 안내 문구를 만들면 "왜 안 보이는가" 를 설명하는 UI 가 새로 생겨 비노출
   * 결정과 어긋난다. QA·디버깅은 이 필드를 응답에서 직접 본다.
   */
  rollbackReason?: string | null
  /**
   * 되감기 API(`/rollback-stay`·`/rollback-settlement`)에 넘길 전환 약정 id.
   * 결제 건에서 유도하지 말고 이 값을 쓴다 — 대상 지목의 주인이 서버다.
   */
  rollbackSubscriptionId?: number | null
  refunds: InvoiceRefund[]
}

/**
 * `GET /v1/admin/payment-orders/{paymentOrderId}/refunds` (`PaymentOrderRefundView`)
 *
 * `InvoiceRefundView` 의 단건판이다. 되감기 칸이 없는 것은 되감을 약정이 없어서다.
 */
export type PaymentOrderRefundView = {
  paymentOrderId: number
  merchantUid: string | null
  price: number
  refundedTotal: number
  unsettledTotal: number
  state: InvoiceRefundState
  refunds: InvoiceRefund[]
}

/** 결제 건 목록·수동 기록이 두 현황에서 공통으로 읽는 칸 */
export type RefundTargetView = Pick<InvoiceRefundView, 'price' | 'refundedTotal' | 'unsettledTotal' | 'state' | 'refunds'>

/** `GET /v1/admin/workspaces/{id}/refund-ledger` 행 (`RefundLedgerRow`) */
export type RefundLedgerRow = {
  refundId: number
  status: InvoiceRefundStatus
  reason: RefundReason
  /**
   * 대상 칸(D-121). 청구서 환불이면 `invoiceId`, 크레딧 팩 결제 건 환불이면 `paymentOrderId` 만
   * 채워진다. 아래 결제고유번호·주문번호·`invoicePrice`·`invoicePaidOn` 은 대상이 무엇이든 같은 칸에
   * 온다 — 칸 이름에 `invoice` 가 남은 것은 화면 계약을 바꾸지 않으려는 서버 결정이다.
   */
  invoiceId: number | null
  paymentOrderId?: number | null
  paymentNumber: string | null
  orderNumber: string | null
  invoicePrice: number | null
  invoicePaidOn: string | null
  amount: number
  amountExcludingVat: number | null
  actualCancelAmount: number | null
  /** 산정액 ≠ 실지급액. 서버가 판정해서 내린다 */
  amountMismatch: boolean
  penalty: number | null
  /**
   * 위약금 **요율**. `penalty` 는 금액이라 요율을 역산할 수 없어 따로 온다.
   *
   * 산정 응답과 같은 **비율** 단위다(`0.1` = 10%). 저장은 퍼센트지만 응답에서 비율로 되돌려
   * 준다(aivatar #506). 구독취소·되감기는 서버 산정이 채우고, 단건·회사 귀책은 수동 기록에서
   * 담당자가 넣는다.
   */
  penaltyRate?: number | null
  /** 위약금 구간 코드. 같은 0% 라도 근거 조항이 갈린다(D-66) */
  penaltyBracket?: PenaltyBracket | null
  /** 단건 사용률(비율). 정책 13.4 공제 근거 — 단건 환불 산정은 다음 단계다 */
  usageRatio?: number | null
  refundedAt: string | null
  channel: RefundChannel | null
  pgTransactionNo: string | null
  memo: string | null
  issuerId: number | null
  issuerEmail: string | null
  refundGroupNo: string | null
  /** 사건이 2행 이상일 때만 값이 있다. 1행 사건은 null */
  pairSequence: number | null
  pairSize: number
  awaitingPair: boolean
  followUpStatus: FollowUpStatus | null
  followUpAt: string | null
  followUpDetail: string | null
  supersedesRefundId: number | null
  createdAt: string
  /** 접수 후 경과일. **달력일이다** — 처리 기한 3영업일 환산은 화면 몫 */
  elapsedDays: number | null
  history: RefundHistoryEntry[]
}

/* ────────────────────────────── 산정 (미리보기) ────────────────────────────── */

/**
 * `GET /v1/admin/subscriptions/{id}/cancellation-refund`
 *
 * 200 이면 `reason` 이 null 이고, 400 이면 금액 필드가 전부 null 이며 `reason` 에 거부 사유가
 * 담긴다. 400 을 실패로 삼키지 말 것 — `quoteOrReason` 을 쓴다.
 */
export type CancellationRefundQuote = {
  subscriptionId: number
  workspaceId: number | null
  planName: string | null
  intervalMonth: number | null
  referenceDate: string
  /**
   * 환불 기록을 붙일 결제 건. **null 이면 결제가 없는 구독**이고 금액은 정가 폴백이라
   * 그대로 환불하면 받은 적 없는 돈이 나간다. 화면에서 경고해야 한다.
   */
  paidInvoiceId: number | null
  paidOn: string | null
  charge: number | null
  /** 산정에 쓴 기준 청구액. `charge` 와 다르면 전환으로 시작된 약정이다 */
  refundBasis: number | null
  usedMonths: number | null
  /** 연간만. 진행 중인 달의 경과일 비율 */
  elapsedRatio: number | null
  /** 월만. 남은 일수 ÷ 30 */
  remainingRatio: number | null
  creditGrant: number | null
  creditConsumed: number | null
  creditUsedRatio: number | null
  penaltyBracket: PenaltyBracket | null
  penaltyRate: number | null
  amountExcludingVat: number | null
  transitionMonth: boolean | null
  withinWithdrawalPeriod: boolean | null
  /** 취소수수료 공제 전 환불액 */
  beforeFee: number | null
  penalty: number | null
  /** 최종 환불액 (부가세 포함) */
  amount: number | null
  calculation: string | null
  reason: string | null
}

/** 크레딧 팩 미사용분을 어디서 셌나. `FALLBACK` 이면 응답의 `warning` 을 담당자가 확인해야 한다 */
export type CreditPackPoolBasis = 'CHECKPOINT' | 'FALLBACK'

/**
 * `GET /v1/admin/payment-orders/{orderId}/credit-pack-refund` (aivatar #339 T-E4 · #537)
 *
 * 구독취소 산정과 같은 규칙이다 — 400 이면 금액이 전부 null 이고 `reason` 이 조치 안내다.
 * `quoteOrReason` 으로 감싼다. 요율·비율은 전부 **비율**(0.05)이다.
 *
 * 검산: `unused = MAX(0, MIN(granted, creditPackRemaining − grantedAfter))`
 */
export type CreditPackRefundQuote = {
  orderId: number
  workspaceId: number | null
  merchantUid: string | null
  /** 구매일 — 청약철회 기산점 */
  purchasedOn: string | null
  /** 소멸일(당일까지 유효) */
  expiresOn: string | null
  referenceDate: string
  /** 구독 종료일 — 종료 선택권 기산점. 구독 중이면 null */
  terminatedOn: string | null
  charge: number | null
  /** 이 결제 건 지급 크레딧 */
  granted: number | null
  unused: number | null
  unusedRatio: number | null
  usageRatio: number | null
  penaltyBracket: PenaltyBracket | null
  penaltyRate: number | null
  beforeFee: number | null
  penalty: number | null
  amount: number | null
  calculation: string | null
  poolBasis: CreditPackPoolBasis | null
  /** 산정은 됐지만 확인이 필요한 사유. 폴백 산정이면 채워진다 */
  warning: string | null
  /** 산정 불가 사유. 산정되면 null */
  reason: string | null
  /** 크레딧 팩 남은 합계(소멸 도래분 제외) */
  creditPackRemaining: number | null
  /** 이 결제 건 이후 결제된 크레딧 팩 지급 합 */
  grantedAfter: number | null
  /** 지금 구독 중인가. 참이면 종료 선택권(구간 2)이 없다 */
  subscribed: boolean | null
  /** 구간 1(청약철회) 마지막 날 — 판정과 같은 셈법(aivatar #554). BE 배포 전에는 없다 */
  withdrawalDeadline?: string | null
  /** 구간 2(종료 선택권) 마지막 날. 구독 중이면 null */
  optionDeadline?: string | null
  /** 구간 표 "조건" 칸의 기한 · 경계 · 요율(요금 기준표 값, 비율은 0~1). 화면에 박지 않는다(환불대장 v4 P2) */
  terms?: CreditPackRefundTerms | null
}

/** 산정 응답의 기준값. 고객 판매 목록 `terms` 와 같은 값이다. 칸이 null 이면 미설정 */
export type CreditPackRefundTerms = {
  withdrawalPeriodDays: number | null
  optionPeriodDays: number | null
  lowUsageThreshold: number | null
  lowUsageRate: number | null
  highUsageRate: number | null
}

/** 구간 표 한 줄 (환불대장 v4 P2 "구간 판정") */
export type CreditPackBracketRow = {
  key: string
  /** "1 청약철회 · 0%" */
  name: string
  /** "구매일 0일, 7일째 당일까지" */
  condition: string
  /** "적용 · 사용률 30.0%" · "해당 없음 (2026-10-07까지)" */
  verdict: string
  applied: boolean
}

/** 0.1 → "10%", 0.05 → "5%". 기준값이 없으면 "?" — 지어낸 숫자를 보이지 않는다 */
const percentOf = (ratio?: number | null): string =>
  ratio == null ? '?' : `${Number((Number(ratio) * 100).toFixed(3))}%`

const daysOf = (days?: number | null): string => (days == null ? '?' : String(days))

/**
 * 네 구간을 위에서부터 보이고, 적용하지 않은 구간은 왜 아닌지 한 줄로 적는다 (환불대장 v4 P2 · 명세 7-2).
 *
 * **판정은 하지 않는다.** 적용 구간은 응답의 `penaltyBracket` 을 그대로 읽고, 미적용 이유의 날짜는
 * 서버가 판정과 같은 셈법으로 낸 `withdrawalDeadline` · `optionDeadline` 이다. 적용 구간보다 아래 줄은
 * 판정까지 가지 않았으므로 "-" 다(1 과 2 가 겹치면 1).
 */
export const creditPackBracketRows = (quote: CreditPackRefundQuote): CreditPackBracketRow[] => {
  const t = quote.terms
  const order = ['WITHIN_WITHDRAWAL', 'TERMINATION_OPTION', 'CREDIT_PACK_LOW_USAGE', 'CREDIT_PACK_HIGH_USAGE'] as const
  const appliedAt = quote.penaltyBracket ? (order as readonly PenaltyBracket[]).indexOf(quote.penaltyBracket) : -1
  const until = (date?: string | null) => (date ? `해당 없음 (${date}까지)` : '해당 없음')

  const notApplied = (bracket: PenaltyBracket): string => {
    switch (bracket) {
      case 'WITHIN_WITHDRAWAL':
        return until(quote.withdrawalDeadline)
      case 'TERMINATION_OPTION':
        return quote.subscribed ? '해당 없음 (구독 중)' : until(quote.optionDeadline)
      default:
        return '해당 없음'
    }
  }
  const appliedText = (bracket: PenaltyBracket): string => {
    switch (bracket) {
      case 'TERMINATION_OPTION':
        return `적용 · 구독 종료일 ${quote.terminatedOn ?? '-'} · 종료 뒤 유료 구독 재개 없음`
      case 'CREDIT_PACK_LOW_USAGE':
      case 'CREDIT_PACK_HIGH_USAGE':
        return `적용 · 사용률 ${formatRatio(quote.usageRatio)}`
      default:
        return '적용'
    }
  }

  const meta: Record<(typeof order)[number], { name: string; condition: string }> = {
    WITHIN_WITHDRAWAL: {
      name: '1 청약철회 · 0%',
      condition: `구매일 0일, ${daysOf(t?.withdrawalPeriodDays)}일째 당일까지`,
    },
    TERMINATION_OPTION: {
      name: '2 종료 선택권 · 0%',
      condition: `구독 종료일 0일, ${daysOf(t?.optionPeriodDays)}일째 당일까지 · 그 사이 재구독 없음`,
    },
    CREDIT_PACK_LOW_USAGE: {
      name: `3-1 사용량 차등 · ${percentOf(t?.lowUsageRate)}`,
      condition: `이 결제 건 사용률 ${percentOf(t?.lowUsageThreshold)} 이하`,
    },
    CREDIT_PACK_HIGH_USAGE: {
      name: `3-2 사용량 차등 · ${percentOf(t?.highUsageRate)}`,
      condition: `이 결제 건 사용률 ${percentOf(t?.lowUsageThreshold)} 초과`,
    },
  }

  return order.map((bracket, index) => ({
    key: bracket,
    name: meta[bracket].name,
    condition: meta[bracket].condition,
    applied: index === appliedAt,
    verdict:
      index === appliedAt ? appliedText(bracket) : appliedAt >= 0 && index > appliedAt ? '-' : notApplied(bracket),
  }))
}

/**
 * 크레딧 팩 산정 구간 → 접수 사유. **고르게 하지 않는다**(전달본 P2).
 *
 * - 구간 2 = 종료 선택권
 * - 구간 1(청약철회)·3-1·3-2 = 단건 크레딧팩. 결제 건 사유에 청약철회가 없다(구독 사유라서)
 * - 소멸 후 재량은 산정이 400 으로 끊는 건이라 여기 오지 않는다 — 수동 기록 경로다
 */
export const creditPackReasonOf = (bracket?: PenaltyBracket | null): RefundReason =>
  bracket === 'TERMINATION_OPTION' ? 'TERMINATION_OPTION' : 'CREDIT_PACK'

/** `GET /v1/admin/subscriptions/{id}/rollback-stay` — 갈아탄 것만 무르고 구독은 계속 */
export type RollbackStayQuote = {
  subscriptionId: number
  referenceDate: string
  conversionCharge: number | null
  usedAmount: number | null
  refundAmount: number | null
  restoredSubscriptionId: number | null
  restoredPlanName: string | null
  restoredAnchor: string | null
  restoredEndAt: string | null
  /**
   * 복원이 **사람에게 넘기는 항목**. 비어 있으면 자동으로 끝난다 (정책 13.6 · D-104).
   *
   * 되살릴 수 없는 예약(무효화 주체·시각이 없거나, 더 이른 전환이 지운 예약이 남은 경우)을
   * 서버가 복원하지 않고 문장으로 넘긴다. 🔴 **접수 전에 보여야 한다** — 종전에는 복원 실행
   * 시점에만 생겨서 "수기로 확인하라" 가 돌이킬 수 없는 실행 뒤에 도착했다(aivatar #512).
   */
  manualFollowUps?: string[] | null
  calculation: string | null
  reason: string | null
}

/** `GET /v1/admin/subscriptions/{id}/rollback-settlement` — 무르고 그만둔다. 결제 건 2개로 분해 */
export type RollbackSettlementQuote = {
  subscriptionId: number
  referenceDate: string
  /** 전환 결제 건에서 취소할 금액 */
  conversionRefund: number | null
  /** 직전 약정 결제 건에서 취소할 금액 */
  priorRefund: number | null
  totalRefund: number | null
  usedAmount: number | null
  /** 검산용. 전환을 거친 고객과 거치지 않은 고객의 순부담이 같아야 정산이 옳다 */
  netBurden: number | null
  calculation: string | null
  reason: string | null
}

/* ────────────────────────────── 요청 본문 ────────────────────────────── */

/**
 * `POST /v1/admin/invoices/{invoiceId}/refunds`
 *
 * `status: 'QUOTED'` 면 접수 행이고 처리 결과 4값(`refundedAt`·`channel`·`actualCancelAmount`
 * ·`pgTransactionNo`)을 **넣으면 400** 이다 — 결제대행사 취소를 아직 실행하지 않았다.
 * 생략하면 처리 완료 행이 되며, 그 경로는 후속 조치를 발화시키지 않는다.
 */
export type RecordRefundRequest = {
  status?: InvoiceRefundStatus
  amount: number
  penalty?: number | null
  reason: RefundReason
  channel?: RefundChannel
  calculation?: string | null
  refundedAt?: string
  refundGroupNo?: string
  pgTransactionNo?: string
  memo?: string
  actualCancelAmount?: number
  /**
   * 위약금 요율. **비율로 넘긴다**(`0.1` = 10%) — 서버가 퍼센트로 바꿔 저장한다.
   * 화면은 담당자에게 % 로 묻고 100 으로 나눠 보낸다.
   */
  penaltyRate?: number | null
  /** 위약금 구간 코드. 금액이 0원이어도 근거 조항은 남아야 한다(D-66) */
  penaltyBracket?: PenaltyBracket
  /** 단건 사용률. 위와 같이 비율이다 */
  usageRatio?: number | null
  /**
   * 이 기록이 정정한 원 환불 기록 (화면 명세 8.4 · aivatar #502).
   *
   * 🔴 **철회된 행만** 지목할 수 있고 **같은 결제 건** 안에서만 성립한다 — 서버가 둘 다 400 으로
   * 막는다. 대장은 행을 지우지 않으므로, 잘못 기입한 행은 철회해 두고 정정 기록이 그것을 가리킨다.
   */
  supersedesRefundId?: number
}

/**
 * `POST /v1/admin/invoice-refunds/{id}/complete`
 *
 * 🔴 이 확정이 후속 조치의 트리거다. `dryRun: true` 로 먼저 무엇이 일어날지 본 뒤에 확정한다.
 * `refundedAt`(처리일)은 비활성 진입일이 되므로 미래도, 결제일보다 이른 값도 400 이다.
 */
export type CompleteRefundRequest = {
  refundedAt: string
  channel: RefundChannel
  actualCancelAmount: number
  pgTransactionNo?: string
  memo?: string
  dryRun?: boolean
}

/** `POST /v1/admin/refunds/rollback` — 금액을 담당자가 넣지 않는다. 서버가 산정해 행을 만든다 */
export type ReceiveRollbackRefundRequest = {
  subscriptionId: number
  referenceDate: string
  /** true = 이탈형(2행 생성), false = 잔류형(1행) */
  exit: boolean
  memo?: string
}

export type RecordRefundResponse = {
  created: InvoiceRefund
  state: InvoiceRefundState
  refundedTotal: number
  unsettledTotal: number
}

export type CompleteRefundResponse = {
  completed: InvoiceRefund
  state: InvoiceRefundState
  refundedTotal: number
  unsettledTotal: number
  followUp: RefundFollowUp | null
}

export type ReceiveRollbackRefundResponse = {
  refundGroupNo: string
  totalRefund: number
  rows: InvoiceRefund[]
  calculation: string | null
}

/* ────────────────────────────── 상태 전이 가부 ────────────────────────────── */

/**
 * 어떤 조치를 열어줄지. **서버 규칙의 미러이지 판정의 주인이 아니다** — 가부는 항상 서버가
 * 정하고, 화면은 눌러봐야 아는 버튼을 줄이기 위해 같은 조건을 앞에서 한 번 본다. 400 이 오면
 * 그 사유를 그대로 띄운다.
 *
 * 되감기 자격(서버 6조건)을 프론트가 복제하지 않기로 한 것과 갈리는 이유는 규칙의 성질이다 —
 * 저쪽은 결제·약정 데이터를 훑어야 답이 나오지만, 이 전이들은 `InvoiceRefund` 엔티티에서
 * **상태 enum 한 값**으로 끝난다(`complete`·`hold`·`withdraw`). 복제해도 갈릴 여지가 작다.
 */

/** 처리 완료 확정. 🔴 접수뿐 아니라 **보류 상태에서도 된다** (`InvoiceRefund.complete`) */
export const canCompleteRefund = (status: InvoiceRefundStatus): boolean => status === 'QUOTED' || status === 'ON_HOLD'

/** 보류는 접수 상태에서만. 보류를 다시 보류할 수는 없다 */
export const canHoldRefund = (status: InvoiceRefundStatus): boolean => status === 'QUOTED'

/** 철회. 처리 완료된 행은 돈이 이미 나갔으므로 제외 — 그 정정은 반대 방향의 새 기록이다 */
export const canWithdrawRefund = (status: InvoiceRefundStatus): boolean =>
  status !== 'COMPLETED' && status !== 'WITHDRAWN'

/**
 * 후속 조치 재시도. 두 조건을 **모두** 만족해야 한다 (`RefundFollowUpService.requireRetryable`).
 *
 * - 행이 처리 완료여야 한다 — 접수 행에는 실행할 조치가 없다
 * - 조치가 실패했거나 짝을 기다리는 중이어야 한다. 이미 실행된 것을 다시 돌리면 구독을 두 번
 *   끄거나 복원을 두 번 한다
 *
 * `followUpStatus` 가 null 인 행(확정 기록이 남기 전 옛 데이터)은 서버가 막지 않으므로 연다.
 */
export const canRetryFollowUp = (status: InvoiceRefundStatus, followUpStatus?: FollowUpStatus | null): boolean =>
  status === 'COMPLETED' &&
  (followUpStatus == null || followUpStatus === 'FAILED' || followUpStatus === 'PENDING_GROUP')

/* ────────────────────────────── 파생 표시 ────────────────────────────── */

/**
 * 결제대행사 취소 종류 — "전액 취소 · 55,000원" / "부분 취소 · 34,650원 (청구액 55,000원 중)"
 * (환불대장 v4 S3 · P3 "취소 종류 · 지시액", PK-01 · 02).
 *
 * 지시액(산정 환불액, 부가세 포함)이 청구액과 같으면 전액 취소, 적으면 부분 취소다. 담당자가 결제대행사 취소
 * 화면에서 전액 · 부분 중 무엇을 고를지 바로 알게 한다. 청구액을 모르면 null.
 */
export const cancelKindLabel = (amount?: number | null, price?: number | null): string | null => {
  if (amount == null || price == null) return null
  const won = (value: number) => `${Number(value).toLocaleString()}원`
  return Number(amount) >= Number(price)
    ? `전액 취소 · ${won(amount)}`
    : `부분 취소 · ${won(amount)} (청구액 ${won(price)} 중)`
}

/**
 * 요율·사용률 표시. 서버는 비율(`0.1`)로 주고 화면은 퍼센트로 읽는다.
 *
 * 산정 미리보기·대장 상세·수동 기록이 같은 값을 적으므로 한 곳에 둔다 — 소수점 자리수가
 * 화면마다 다르면 같은 건을 놓고 대조할 수 없다.
 */
export const formatRatio = (value?: number | null): string =>
  value == null ? '-' : `${(Number(value) * 100).toFixed(1)}%`

/**
 * 결제 건 목록의 "환불 상태" 4값.
 *
 * `InvoiceRefundState` 만으로는 "접수됨" 이 안 보인다 — 그 집계는 **처리 완료 행만** 세기
 * 때문에 접수만 걸려 있는 청구서도 `NONE` 으로 온다. 그래서 `unsettledTotal` 을 함께 본다.
 */
export const resolveInvoiceRefundStatus = (
  view?: Pick<RefundTargetView, 'state' | 'unsettledTotal'> | null,
): { label: string; color: string } => {
  if (!view) return { label: '-', color: 'default' }

  switch (view.state) {
    case 'PARTIAL':
      return { label: '부분 환불', color: 'orange' }
    case 'FULL':
      return { label: '환불 완료', color: 'purple' }
    case 'OVER_REFUNDED':
      return { label: '초과 환불', color: 'red' }
    default:
      return view.unsettledTotal > 0
        ? { label: '접수됨', color: 'gold' }
        : { label: '없음', color: 'default' }
  }
}

/**
 * 표시 상태는 두 축의 합성이다. `InvoiceStatus` 는 "이 결제 시도가 성공했나",
 * `InvoiceRefundState` 는 "얼마나 돌려줬나" 로 축이 다르다.
 * "부분 환불된 PAID 결제" 를 상태 하나로 표현할 수 없다.
 */
export const resolveInvoiceDisplayStatus = (
  status?: InvoiceStatus | string | null,
  refundState?: InvoiceRefundState | null,
): { label: string; color: string } => {
  const paid = String(status ?? '').toUpperCase() === 'PAID'

  if (!paid) return { label: '결제 실패', color: 'red' }

  switch (refundState) {
    case 'PARTIAL':
      return { label: '부분 환불', color: 'orange' }
    case 'FULL':
      return { label: '전액 환불', color: 'purple' }
    case 'OVER_REFUNDED':
      return { label: '초과 환불', color: 'red' }
    case 'NONE':
      return { label: '결제 완료', color: 'green' }
    default:
      return { label: '결제 완료(환불 조회 불가)', color: 'green' }
  }
}

/**
 * 사용분 금액. 산정 응답에 직접 없어 역산한다.
 *
 * `refundBasis` 는 산정 기준 청구액(연간은 차감 전 정가성 연 할인가), `beforeFee` 는 취소수수료
 * 공제 전 환불액이다.
 *
 * dev 실측(2026-09-15):
 * - 월 `sub 12411` — `31,900 − 19,140 = 12,760` (경과 40%). `calculation` 과 맞는다
 * - 연 명세 예시 — `950,400 − 158,400 = 792,000`. 맞는다
 *
 * 🔴 **항상 "사용분" 인 것은 아니다.** 월 구독의 서버 산정은 `MIN(기간 축, 크레딧 축)` 이라
 * (`"MIN(기간 19140원, 크레딧 31900원)"`) 크레딧 축이 이기는 건에서는 `beforeFee` 가 시간
 * 기준이 아니게 되고, 이 뺄셈도 시간 사용분을 뜻하지 않는다. 그래서 화면은 이 값을
 * **"역산"** 으로 표시하고 `calculation` 원문을 반드시 함께 노출한다 — 근거는 서버 문장이지
 * 이 계산이 아니다.
 */
export const resolveUsedAmount = (quote: CancellationRefundQuote): number | null => {
  if (quote.refundBasis == null || quote.beforeFee == null) return null
  return quote.refundBasis - quote.beforeFee
}
